import { useAtom } from 'jotai';
import { ChangeEvent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authAtom } from '../../atoms';
import { projectAtom } from '../../atoms/projectAtom';
import {
  useProjectsQuery,
  useRefreshTokenMutation,
  useUpdateLanguageMutation,
} from '../../lib/queries';
import { Select } from '../Select';
import { CreateMenu } from './CreateMenu';
import { UserMenu } from './UserMenu';
import { PATHS } from '../../lib/paths';
import { QUERY_KEYS, TOAST_DURATION } from '../../lib/constants';
import { isClient } from '../../lib/utils';
import LanguageSelector from '../LanguageSelector';
import i18n from '../../i18n';

export function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [project, setProject] = useAtom(projectAtom);
  const [auth, setAuth] = useAtom(authAtom);
  const projectsQuery = useProjectsQuery();
  const queryClient = useQueryClient();

  const updateLanguageMutation = useUpdateLanguageMutation();
  const refreshTokenMutation = useRefreshTokenMutation();

  const isActiveNavLink = useCallback(
    ({ isActive }: any) => (isActive ? 'text-primary-500' : ''),
    [],
  );

  const handleProjectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setProject(projectsQuery.data?.projects.find((p) => p.id === e.target.value));
  };

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const language = event.target.value;
    updateLanguageMutation.mutate(language, {
      onSuccess: async () => {
        setAuth((prevAuth) => {
          if (prevAuth?.user) {
            return {
              ...prevAuth,
              user: { ...prevAuth.user, language },
            };
          }

          return prevAuth;
        });
        i18n.changeLanguage(language);
        if (auth?.refreshToken) {
          try {
            await refreshTokenMutation.mutateAsync({ refreshToken: auth.refreshToken });
            toast.success(t('settings.languageChange'), {
              duration: TOAST_DURATION,
            });
          } catch (err) {
            toast.error(t('settings.languageChangeError'), {
              duration: TOAST_DURATION,
            });
          }
        }
      },
      onError: () => {
        toast.error(t('settings.languageChangeError'), {
          duration: TOAST_DURATION,
        });
      },
    });
  };

  useEffect(() => {
    if (
      projectsQuery.data &&
      (!project ||
        !projectsQuery.data.projects.some((queryProject) => project.id === queryProject.id))
    )
      setProject(projectsQuery.data.projects[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsQuery.data, project]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECTS] });
  }, [auth?.user, queryClient]);

  if (!auth?.user) return null;

  return (
    <nav className="grid grid-cols-[minmax(max-content,1fr)_minmax(max-content,700px)_minmax(max-content,1fr)] items-center gap-4 p-4 font-semibold shadow">
      <img
        className="mr-auto cursor-pointer h-9 invert-[80%]"
        src="/logo.png"
        alt=""
        onClick={() => navigate('/')}
      />

      <ul className="flex gap-10 items-center">
        <li className="uppercase">
          <NavLink to={PATHS.reportsPath()} className={isActiveNavLink}>
            {t('reports.title')}
          </NavLink>
        </li>
        {!isClient(auth.user) && (
          <>
            <li className="uppercase">
              <NavLink to={PATHS.projectsPath()} className={isActiveNavLink}>
                {t('projects.title')}
              </NavLink>
            </li>
            <li className="uppercase">
              <NavLink to={PATHS.usersPath()} className={isActiveNavLink}>
                {t('users.title')}
              </NavLink>
            </li>
            <li className="uppercase ml-auto">
              <CreateMenu />
            </li>
          </>
        )}
      </ul>

      <div className="flex justify-end gap-4">
        <Select
          className="bg-transparent max-w-[10rem]"
          value={project?.id}
          onChange={handleProjectChange}
        >
          {projectsQuery.data?.projects.map((p) => (
            <option className="text-neutral-900" key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <LanguageSelector onChange={handleLanguageChange} value={i18n.language} />
        <UserMenu />
      </div>
    </nav>
  );
}
